/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 *
 * This file is proprietary and confidential. Unauthorized copying,
 * distribution, modification, or use of this file, via any medium,
 * is strictly prohibited without the express written permission of
 * SKYXOPS Corp.
 * ============================================================================= */
import { useEffect, useState } from 'react';
import {
  Alert, AlertDescription, AlertTitle, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input,
} from '@skyxops/ui';
import { ApiError, changePassword, disconnectSso, getProfile, type Profile } from '../../api';
import { authOrigin } from '../../authOrigin';
import { MfaControl } from './MfaControl';

const MIN_LEN = 12;

function strength(v: string): { pct: number; label: string; color: string } {
  let s = 0;
  if (v.length >= MIN_LEN) s++;
  if (/[A-Z]/.test(v)) s++;
  if (/[a-z]/.test(v)) s++;
  if (/[0-9]/.test(v)) s++;
  if (/[^A-Za-z0-9]/.test(v)) s++;
  return {
    pct: [0, 20, 40, 60, 80, 100][s],
    label: ['—', 'Very weak', 'Weak', 'Fair', 'Good', 'Strong'][s],
    color: ['#ef4444', '#ef4444', '#f59e0b', '#f59e0b', '#00D4AA', '#00D4AA'][s],
  };
}

const PROVIDER_LABEL: Record<string, string> = { microsoft: 'Microsoft', google: 'Google' };

const LINK_PENDING_KEY = 'skyxops_sso_link_pending';

function consumeLinkResult(): string | null {
  const q = new URLSearchParams(window.location.search);
  const pending = window.localStorage.getItem(LINK_PENDING_KEY);
  window.localStorage.removeItem(LINK_PENDING_KEY);
  if (q.get('linked') === '1') return 'Sign-in method connected.';
  if (q.get('link_error')) return `Couldn’t connect: ${q.get('link_error')!.replace(/_/g, ' ')}.`;
  if (pending) {
    const label = PROVIDER_LABEL[pending] ?? pending;
    return `Couldn’t connect ${label} — make sure your email has a ${label} account, then try again.`;
  }
  return null;
}

function linkUrl(provider: 'microsoft' | 'google'): string {
  return `${authOrigin()}/api/v1/auth/sso/start?provider=${provider}&link=1`;
}

const GOOGLE_ICON = 'https://appstatic.skyxops.io/login-icons/Google.png';
const MICROSOFT_ICON = 'https://appstatic.skyxops.io/login-icons/Microsoft.png';

function MsIcon() {
  return <img src={MICROSOFT_ICON} alt="" className="h-6 w-6" aria-hidden />;
}

function GoogleIcon() {
  return <img src={GOOGLE_ICON} alt="" className="h-6 w-6" aria-hidden />;
}

function ProviderRow({ provider, detail, action }: { provider: string; detail: React.ReactNode; action: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
      {provider === 'google' ? <GoogleIcon /> : <MsIcon />}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{PROVIDER_LABEL[provider] ?? provider}</div>
        <div className="truncate text-xs text-muted-foreground">{detail}</div>
      </div>
      {action}
    </div>
  );
}

const CONNECTED_BADGE = (
  <span className="inline-flex items-center gap-1 rounded border border-skyxops-beach/40 px-1.5 py-0.5 text-[0.65rem] font-semibold text-skyxops-beach">
    ✓ Connected
  </span>
);

export function SecuritySection({ profile, onProfileChange }: { profile: Profile; onProfileChange?: (p: Profile) => void }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const [ssoNotice, setSsoNotice] = useState<string | null>(consumeLinkResult);

  // Coming back from the IdP via the Back button restores this page from the
  // bfcache — React state is frozen mid-navigation and the initializer above
  // never re-runs (the page looks stuck "loading" until a manual refresh).
  // pageshow(persisted) + focus re-consume the pending marker and unfreeze.
  useEffect(() => {
    const recheck = () => {
      const n = consumeLinkResult();
      if (n) setSsoNotice(n);
    };
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) recheck(); };
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', recheck);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', recheck);
    };
  }, []);

  // Connect opens the IdP in a new tab; THIS page stays interactive and shows
  // an inline "Connecting..." state while polling the profile (server state is
  // the truth). 20s without a binding -> couldn't-connect notice, tab closed.
  const [linking, setLinking] = useState<string | null>(null);
  function startLink(provider: 'microsoft' | 'google') {
    const label = PROVIDER_LABEL[provider];
    const w = window.open(linkUrl(provider), '_blank');
    setLinking(provider);
    setSsoBusy(true);
    setSsoNotice(null);
    const startedAt = Date.now();
    const timer = window.setInterval(async () => {
      let connected = false;
      try {
        const p = await getProfile();
        connected = !!p.sso_connected;
        if (connected) onProfileChange?.(p);
      } catch { /* transient poll error — keep waiting */ }
      const closed = !!w && w.closed;
      const timedOut = Date.now() - startedAt > 20_000;
      if (connected || closed || timedOut) {
        window.clearInterval(timer);
        try { if (w && !w.closed) w.close(); } catch { /* cross-origin — ignore */ }
        setSsoBusy(false);
        setLinking(null);
        setSsoNotice(connected
          ? 'Sign-in method connected.'
          : `Couldn’t connect ${label} — make sure your email has a ${label} account, then try again.`);
      }
    }, 2000);
  }

  async function onDisconnect() {
    setSsoBusy(true); setSsoNotice(null);
    try {
      const p = await disconnectSso();
      onProfileChange?.(p);
      setSsoNotice('Sign-in method disconnected.');
    } catch (err) {
      setSsoNotice(err instanceof ApiError ? err.message : 'Could not disconnect.');
    } finally { setSsoBusy(false); }
  }

  const st = strength(next);
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = !!cur && next.length >= MIN_LEN && next === confirm && !busy;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setDone(false);
    try {
      await changePassword(cur, next);
      setCur(''); setNext(''); setConfirm(''); setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change password.');
    } finally { setBusy(false); }
  }

  const label = 'text-sm font-medium text-foreground';

  const providerLabel = PROVIDER_LABEL[profile.sso_provider ?? ''] ?? 'your identity provider';

  // Federated-only account: password & 2FA live at the IdP — show the
  // sign-in-method card instead of the password/2FA cards.
  if (profile.is_federated && profile.has_password === false) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sign-in method</CardTitle>
          <CardDescription>How you sign in to SKYXOPS.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ProviderRow
            provider={profile.sso_provider ?? 'sso'}
            detail={<>{profile.email}{profile.sso_connected ? ' · linked to this account' : ''}</>}
            action={CONNECTED_BADGE}
          />
          <p className="text-sm text-muted-foreground">
            Your password and two-factor authentication are managed by your{' '}
            <span className="font-medium text-foreground">{providerLabel}</span> account.
            SKYXOPS never stores a password for this profile.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled>Connect another sign-in method <span className="ml-1.5 rounded bg-muted px-1 text-[0.6rem] text-muted-foreground">SOON</span></Button>
            <Button variant="outline" disabled>Set a backup password <span className="ml-1.5 rounded bg-muted px-1 text-[0.6rem] text-muted-foreground">SOON</span></Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Two-factor authentication</CardTitle>
          <CardDescription>A second step at sign-in keeps your account secure.</CardDescription>
        </CardHeader>
        <CardContent><MfaControl /></CardContent>
      </Card>

      {!profile.is_federated && (
        <Card>
          <CardHeader>
            <CardTitle>Password</CardTitle>
            <CardDescription>Choose a strong password you don’t use anywhere else.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              {error && <Alert variant="destructive"><AlertTitle>Couldn’t update password</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
              {done && <Alert className="border-skyxops-beach/40 bg-skyxops-beach/10"><AlertDescription className="text-sm">Your password has been updated.</AlertDescription></Alert>}
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <label htmlFor="cur" className={label}>Current password</label>
                  <Input id="cur" type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} disabled={busy} required />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="np" className={label}>New password</label>
                  <Input id="np" type="password" autoComplete="new-password" placeholder={`At least ${MIN_LEN} characters`} value={next} onChange={(e) => setNext(e.target.value)} disabled={busy} required />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="cf" className={label}>Confirm new password</label>
                  <Input id="cf" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy} required />
                </div>
              </div>
              <div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full transition-all" style={{ width: `${st.pct}%`, backgroundColor: st.color }} />
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Strength: {st.label}{mismatch ? ' · passwords don’t match' : ''}</span>
                  <span className="text-xs text-muted-foreground">{MIN_LEN}+ chars · upper · lower · number · symbol</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">You’ll stay signed in on this device.</span>
                <Button type="submit" className="bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90" disabled={!canSubmit}>
                  {busy ? 'Updating…' : 'Update password'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Connected sign-in methods</CardTitle>
          <CardDescription>Sign in with your work Microsoft or Google account as well as your password.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {ssoNotice && (
            <Alert className="border-skyxops-beach/40 bg-skyxops-beach/10">
              <AlertDescription className="text-sm">{ssoNotice}</AlertDescription>
            </Alert>
          )}
          {profile.sso_connected ? (
            <ProviderRow
              provider={profile.sso_provider ?? 'sso'}
              detail={<>{profile.email} · connected</>}
              action={
                <div className="flex items-center gap-2">
                  {CONNECTED_BADGE}
                  <Button variant="ghost" size="sm" disabled={ssoBusy} onClick={onDisconnect}>
                    {ssoBusy ? '…' : 'Disconnect'}
                  </Button>
                </div>
              }
            />
          ) : (
            <>
              <ProviderRow provider="microsoft" detail="Sign in with your work Microsoft account"
                action={<Button variant="outline" size="sm" disabled={ssoBusy} onClick={() => startLink('microsoft')}>{linking === 'microsoft' ? 'Connecting…' : 'Connect'}</Button>} />
              <ProviderRow provider="google" detail="Sign in with your Google account"
                action={<Button variant="outline" size="sm" disabled={ssoBusy} onClick={() => startLink('google')}>{linking === 'google' ? 'Connecting…' : 'Connect'}</Button>} />
            </>
          )}
          <p className="text-xs text-muted-foreground">
            Once connected, you can sign in either way. Removing your password later requires at least one connected provider.
          </p>
        </CardContent>
      </Card>
    </>
  );
}
